import React, { useEffect, useRef } from "react";
import "./GameLoader.css";

const COLORS = ['#ffd700', '#fff176', '#ffffff', '#82b1ff', '#e3f2fd', '#ffca28', '#ffe082'];

export default function GameLoader() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const introRef = useRef<HTMLAudioElement>(null);

  // Try autoplay once on mount. If the browser blocks (no prior gesture),
  // the first click/key anywhere on the document will unlock — silently.
  useEffect(() => {
    if (introRef.current) {
      introRef.current.volume = 0.6;
      introRef.current.play().catch(() => { /* autoplay blocked — wait for input */ });
    }
    const unlock = () => {
      if (introRef.current) {
        introRef.current.volume = 0.6;
        introRef.current.play().catch(() => {});
      }
      document.removeEventListener('pointerdown', unlock);
      document.removeEventListener('keydown', unlock);
    };
    document.addEventListener('pointerdown', unlock);
    document.addEventListener('keydown', unlock);
    return () => {
      document.removeEventListener('pointerdown', unlock);
      document.removeEventListener('keydown', unlock);
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId: number;
    let running = true;

    function resize() {
      canvas!.width = window.innerWidth;
      canvas!.height = window.innerHeight;
    }
    resize();
    window.addEventListener('resize', resize);

    interface Piece {
      x: number; y: number; w: number; h: number;
      color: string; vx: number; vy: number;
      angle: number; va: number; opacity: number; born: number;
    }

    const pieces: Piece[] = [];
    const MAX = 120;
    let lastSpawn = 0;

    function spawn(t: number) {
      if (pieces.length >= MAX) return;
      pieces.push({
        x: Math.random() * canvas!.width, y: -12,
        w: 6 + Math.random() * 8, h: 4 + Math.random() * 5,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        vx: (Math.random() - 0.5) * 1.6, vy: 1.8 + Math.random() * 2.5,
        angle: Math.random() * Math.PI * 2, va: (Math.random() - 0.5) * 0.12,
        opacity: 0.85 + Math.random() * 0.15, born: t,
      });
    }

    function draw(t: number) {
      if (!running) return;
      ctx!.clearRect(0, 0, canvas!.width, canvas!.height);
      if (t - lastSpawn > 80 && pieces.length < MAX) {
        for (let i = 0; i < 4; i++) spawn(t);
        lastSpawn = t;
      }
      for (let i = pieces.length - 1; i >= 0; i--) {
        const p = pieces[i];
        p.x += p.vx; p.y += p.vy; p.angle += p.va; p.vy += 0.04;
        const fadeStart = canvas!.height * 0.75;
        if (p.y > fadeStart) p.opacity = Math.max(0, p.opacity - 0.018);
        if (p.y > canvas!.height + 20 || p.opacity <= 0) { pieces.splice(i, 1); continue; }
        ctx!.save();
        ctx!.globalAlpha = p.opacity;
        ctx!.translate(p.x, p.y);
        ctx!.rotate(p.angle);
        ctx!.fillStyle = p.color;
        ctx!.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx!.restore();
      }
      animId = requestAnimationFrame(draw);
    }

    animId = requestAnimationFrame(draw);
    return () => {
      running = false;
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <div className="game-loader">
      <canvas ref={canvasRef} className="confetti-canvas" />
      <div className="gl-content">
        <div className="gamy-header">
          <div className="gamy-title">
            {'GAMY JEOPARDY'.split('').map((ch, i) => (
              <span key={i} className="gamy-letter" style={{ animationDelay: `${i * 60}ms` }}>
                {ch === ' ' ? '\u00A0' : ch}
              </span>
            ))}
          </div>
          <div className="gamy-subtitle">90th Birthday Celebration</div>
        </div>

        <div className="gamy-waiting">
          <span className="gamy-waiting-dot" />
          <span className="gamy-waiting-dot" style={{ animationDelay: '0.2s' }} />
          <span className="gamy-waiting-dot" style={{ animationDelay: '0.4s' }} />
        </div>
        <div className="gamy-waiting-text">Waiting for host to start…</div>

        <div className="gamy-footer">
          Celebrating 90 years of Sallyann Sack-Pikus
        </div>
      </div>
      <audio ref={introRef} src="/sounds/jeopardy-intro.mp3" preload="auto" />
    </div>
  );
}
